import {
  Search as SearchIcon,
  StarRate as StarRateIcon
} from '@mui/icons-material';
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
  Tooltip
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';

import {
  Company,
  CounterpartyDropDown,
  CounterpartyType,
  Invoice
} from 'openapi';

import { useCompanies } from 'context/CompanyContext';
import { useTranslations } from 'context/TranslationContext';

import { useDebounce } from 'hooks/useDebounce';

import { COUNTERPARTY_SEARCH_MIN_CHARACTERS } from 'utils/constants/invoices';

import { verificationFormItemNoHeight } from 'styles/pages/InvoiceVerificationStyle';

interface EditCounterpartyFieldProps {
  currentInvoice: Invoice;
  value?: number;
  onChange: (counterpartyId?: number) => void;
}

export const EditCounterpartyField = ({
  currentInvoice,
  value,
  onChange
}: EditCounterpartyFieldProps) => {
  const { translate } = useTranslations();
  const { getAllDropDownCounterpartiesByCompany } =
    useAdditionalFieldsController();
  const companyName = useCompanies().companiesList.find(
    (company: Company) => company.id === currentInvoice.companyId
  )?.name;

  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useState('');
  const [options, setOptions] = useState<CounterpartyDropDown[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const initialOption = useMemo<CounterpartyDropDown | null>(() => {
    if (!currentInvoice.counterpartyId) {
      return null;
    }

    return {
      id: currentInvoice.counterpartyId,
      name: currentInvoice.counterpartyName || '',
      registrationNumber: currentInvoice.counterpartyRegistrationNumber,
      vatNumber: currentInvoice.counterpartyVatNumber
    };
  }, [
    currentInvoice.counterpartyId,
    currentInvoice.counterpartyName,
    currentInvoice.counterpartyRegistrationNumber,
    currentInvoice.counterpartyVatNumber
  ]);

  const [selectedOption, setSelectedOption] = useState<
    CounterpartyDropDown | undefined
  >(initialOption ?? undefined);

  useEffect(() => {
    if (value === selectedOption?.id) {
      return;
    }

    if (!value) {
      setSelectedOption(undefined);
      return;
    }

    if (initialOption?.id === value) {
      setSelectedOption(initialOption);
    }
  }, [initialOption, selectedOption?.id, value]);

  const notEnoughCharactersToSearch =
    searchParams.length < COUNTERPARTY_SEARCH_MIN_CHARACTERS;

  const fetchCounterparties = useCallback(async () => {
    if (!currentInvoice.companyId || notEnoughCharactersToSearch) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    try {
      const result = await getAllDropDownCounterpartiesByCompany(
        Number(currentInvoice.companyId),
        CounterpartyType.SUPPLIER,
        searchParams
      );
      setOptions(result);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentInvoice.companyId,
    getAllDropDownCounterpartiesByCompany,
    notEnoughCharactersToSearch,
    searchParams
  ]);

  const debouncedSearch = useDebounce(fetchCounterparties, 500);

  useEffect(() => {
    debouncedSearch();
  }, [debouncedSearch]);

  return (
    <Autocomplete
      fullWidth
      disableClearable
      blurOnSelect
      clearOnBlur={false}
      forcePopupIcon={false}
      open={
        open &&
        !isLoading &&
        (notEnoughCharactersToSearch || options.length > 0)
      }
      value={selectedOption}
      options={options}
      loading={notEnoughCharactersToSearch ? false : isLoading}
      isOptionEqualToValue={(option, optionValue) =>
        option.id === optionValue.id
      }
      getOptionLabel={(option) => option.name || ''}
      noOptionsText={
        notEnoughCharactersToSearch
          ? translate('messages.searchRequiredCharacters', {
              numberOfCharacters: String(COUNTERPARTY_SEARCH_MIN_CHARACTERS)
            })
          : translate('labels.noCounterpartiesFound')
      }
      onChange={(_event, newValue) => {
        setSelectedOption(newValue);
        onChange(newValue?.id);
      }}
      onInputChange={(_event, newValue, reason) => {
        if (reason !== 'input') {
          return;
        }
        setSearchParams(newValue);
        setIsLoading(newValue.length >= COUNTERPARTY_SEARCH_MIN_CHARACTERS);
      }}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate('labels.counterparty')}
          sx={verificationFormItemNoHeight}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment sx={{ ml: 1 }} position="start">
                  <SearchIcon />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
              </InputAdornment>
            )
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Tooltip
            key={key}
            title={
              option.isCompanyCounterparty
                ? translate('messages.counterpartyOf', {
                    companyName: companyName || ''
                  })
                : ''
            }
          >
            <li {...rest}>
              {option.name || ''}
              {option.isCompanyCounterparty && (
                <StarRateIcon
                  color="primary"
                  data-testid="counterparty-company-star"
                  sx={{ ml: 1, mb: 0.5 }}
                />
              )}
            </li>
          </Tooltip>
        );
      }}
    />
  );
};
